<template>
  <b-modal
    modal-class="component-modal-product"
    id="goodsReceiveItemModal"
    ref="goodsReceiveItemModal"
    size="xl"
    title="รายการใบรับสินค้า"
    no-close-on-backdrop
    @close="close"
  >
    <b-form id="goodsReceiveForm" @submit.prevent="saveGoodsReceive">
      <!-- รหัสใบสั่งซื้ออ้างอิง -->
      <b-form-group
        label-cols-sm="3"
        label="รหัสใบสั่งซื้ออ้างอิง*:"
        label-align-sm="right"
      >
        <b-form-input
          v-model="form.poRefCode"
          placeholder="กรอกรหัสใบสั่งซื้ออ้างอิง เฉพาะภาษาอังกฤษพิมพ์ใหญ่, ตัวเลข, และ อักขระพิเศษ เท่านั้น"
          :class="{ 'is-invalid': $v.form.poRefCode.$error }"
          maxlength="20"
          class="required-input"
          style="text-transform: uppercase"
          @keypress="preventInvalidKey"
          @change="formatPoRefCode"
        ></b-form-input>
      </b-form-group>

      <!-- รหัสผู้ขาย -->
      <b-form-group
        label-cols-sm="3"
        label="รหัสผู้ขาย*:"
        label-align-sm="right"
      >
        <b-form-input
          v-model="form.vendorCode"
          placeholder="กรอกรหัสผู้ขาย เฉพาะภาษาอังกฤษพิมพ์ใหญ่, ตัวเลข, และ อักขระพิเศษ เท่านั้น"
          :class="{ 'is-invalid': $v.form.vendorCode.$error }"
          maxlength="20"
          class="required-input"
          style="text-transform: uppercase"
          @keypress="preventInvalidKey"
          @change="formatVendorCode"
        ></b-form-input>
      </b-form-group>

      <!-- ชื่อผู้ขาย -->
      <b-form-group
        label-cols-sm="3"
        label="ชื่อผู้ขาย*:"
        label-align-sm="right"
      >
        <b-form-input
          v-model="form.vendorName"
          placeholder="กรอกชื่อผู้ขาย"
          :class="{ 'is-invalid': $v.form.vendorName.$error }"
          maxlength="100"
          class="required-input"
        ></b-form-input>
      </b-form-group>

      <!-- Product items table -->
      <b-table
        id="goodsReceiveProductTable"
        ref="goodsReceiveProductTable"
        responsive
        hover
        small
        sort-icon-left
        head-variant="light"
        :items="form.items"
        :fields="fields"
      >
        <template v-slot:head(product)>
          รหัสสินค้า - ชื่อสินค้า<span class="required-star">*</span>
        </template>
        <template v-slot:head(quantity)>
          จำนวนสินค้า<span class="required-star">*</span>
        </template>
        <template v-slot:head(unit)>
          หน่วยสินค้า<span class="required-star">*</span>
        </template>
        <template v-slot:head(totalPrice)>
          ราคารวม<span class="required-star">*</span>
        </template>

        <template v-slot:cell(index)="row">{{ row.index + 1 }}.</template>

        <template v-slot:cell(product)="row">
          <multiselect
            class="col p-0 product-multiselect"
            :class="{
              'is-invalid':
                $v.form.items.$each[row.index] &&
                $v.form.items.$each[row.index].product.$error,
            }"
            v-model="row.item.product"
            :options="getProductOptionsForRow(row.index)"
            :multiple="false"
            placeholder="เลือกสินค้า"
            track-by="productId"
            :custom-label="productLabel"
            selectLabel="เลือกสินค้า"
            selectedLabel="เลือกแล้ว"
            deselectLabel="คลิกเพื่อลบออก"
            openDirection="below"
            @open="repositionDropdown(row.index)"
            @input="onProductSelected(row.index)"
          >
            <span slot="noResult">ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา</span>
          </multiselect>
        </template>

        <template v-slot:cell(quantity)="row">
          <b-form-input
            v-model="row.item.quantity"
            placeholder="กรอกจำนวน"
            :disabled="!row.item.product"
            :class="{
              'is-invalid':
                !!row.item.product &&
                $v.form.items.$each[row.index] &&
                $v.form.items.$each[row.index].quantity.$error,
            }"
            class="required-input"
            min="1"
            inputmode="numeric"
            pattern="[0-9]*"
            @keydown="allowOnlyNumber($event)"
          ></b-form-input>
        </template>

        <template v-slot:cell(unit)="row">
          <multiselect
            class="col p-0"
            :class="{
              'is-invalid':
                !!row.item.product &&
                $v.form.items.$each[row.index] &&
                $v.form.items.$each[row.index].unit.$error,
            }"
            v-model="row.item.unit"
            :options="getUnitOptionsForRow(row.item)"
            :multiple="false"
            :disabled="!row.item.product"
            placeholder="เลือกหน่วยสินค้า"
            track-by="id"
            :custom-label="customLabel"
            selectLabel=""
            selectedLabel=""
            deselectLabel=""
            :allow-empty="false"
            openDirection="below"
            @open="repositionUnitDropdown(row.index)"
          >
            <span slot="noResult">ไม่พบรายการ แนะนำเปลี่ยนคำค้นหา</span>
          </multiselect>
        </template>

        <template v-slot:cell(totalPrice)="row">
          <b-input-group>
            <b-form-input
              v-model="row.item.totalPrice"
              placeholder="กรอกราคารวม"
              :disabled="!row.item.product"
              :class="{
                'is-invalid':
                  !!row.item.product &&
                  $v.form.items.$each[row.index] &&
                  $v.form.items.$each[row.index].totalPrice.$error,
              }"
              class="required-input"
              inputmode="decimal"
              @keydown="allowOnlyDecimal($event)"
            ></b-form-input>
            <template v-slot:append>
              <b-input-group-text class="append-text">บาท</b-input-group-text>
            </template>
          </b-input-group>
        </template>

        <template v-slot:cell(lotNumber)="row">
          <b-form-input
            v-model="row.item.lotNumber"
            :disabled="!row.item.product"
            placeholder="กรอกเลขล็อต"
          ></b-form-input>
        </template>

        <template v-slot:cell(expiredDt)="row">
          <date-picker
            v-model="row.item.expiredDt"
            type="date"
            format="DD MMM YY"
            placeholder="วว/ดด/ปป"
            :disabled="!row.item.product"
            input-class="form-control d-block"
          ></date-picker>
        </template>

        <template v-slot:cell(buttons)="row">
          <b-button
            variant="outline-danger"
            :disabled="form.items.length <= 1"
            @click="removeItem(row.index)"
          >
            <i class="fas fa-trash-alt"></i>
          </b-button>
        </template>

        <template v-slot:bottom-row>
          <td colspan="7" class="bottom-row-cell">
            <b-button
              type="button"
              variant="outline-primary"
              @click="addItem"
              class="bottom-add-row"
            >
              <i class="fas fa-plus"></i> เพิ่มสินค้า
            </b-button>
          </td>
        </template>
      </b-table>
    </b-form>

    <template v-slot:modal-footer>
      <b-button v-if="isEdit" variant="danger" @click="showDeleteConfirm">
        ลบรายการ
      </b-button>
      <b-button type="submit" form="goodsReceiveForm" variant="primary">
        {{ isEdit ? "แก้ไขรายการ" : "สร้างรายการ" }}
      </b-button>
    </template>

    <!-- Delete confirmation modal -->
    <b-modal
      id="deleteConfirmModalItem"
      ref="deleteConfirmModalItem"
      no-close-on-backdrop
      hide-header-close
    >
      <template v-slot:modal-header="{ close }">
        <h5 class="modal-title">
          <i class="fas fa-exclamation-circle text-danger mr-2"></i>
          ลบใบรับสินค้า
        </h5>
        <button type="button" class="close" @click="close">
          <span aria-hidden="true">&times;</span>
        </button>
      </template>
      <p class="mb-0">คุณต้องการลบใบรับสินค้านี้ใช่หรือไม่</p>
      <template v-slot:modal-footer>
        <b-button
          variant="outline-secondary"
          @click="$bvModal.hide('deleteConfirmModalItem')"
        >
          ย้อนกลับ
        </b-button>
        <b-button variant="danger" @click="confirmDelete"> ลบรายการ </b-button>
      </template>
    </b-modal>

    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </b-modal>
</template>

<script>
import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import { required } from "vuelidate/lib/validators";
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import {
  alphaNumUpperSymbol,
  preventInvalidKey,
  formatCodeInput,
} from "@/utils/inputCodeValidator";

export default {
  name: "GoodsReceiveItem",
  components: {
    Dialog,
    Loading,
  },
  data() {
    return {
      isLoading: false,
      isEdit: false,
      editId: null,
      productList: [],
      form: {
        poRefCode: "",
        vendorCode: "",
        vendorName: "",
        items: [this.createEmptyItem()],
      },
      fields: [
        {
          key: "index",
          label: "",
          class: "text-center",
          thStyle: { width: "4%", minWidth: "40px" },
        },
        {
          key: "product",
          label: "รหัสสินค้า - ชื่อสินค้า",
          thStyle: { width: "25%", minWidth: "200px" },
          tdClass: "td-product-col",
        },
        {
          key: "quantity",
          label: "จำนวนสินค้า",
          thStyle: { width: "10%", minWidth: "130px" },
        },
        {
          key: "unit",
          label: "หน่วยสินค้า",
          thStyle: { width: "15%", minWidth: "170px" },
          tdClass: "td-unit-col",
        },
        {
          key: "totalPrice",
          label: "ราคารวม",
          thStyle: { width: "15%", minWidth: "160px" },
        },
        {
          key: "lotNumber",
          label: "เลขล็อต",
          thStyle: { width: "12%", minWidth: "110px" },
        },
        {
          key: "expiredDt",
          label: "วันหมดอายุ",
          thStyle: { width: "13%", minWidth: "130px" },
        },
        {
          key: "buttons",
          label: "",
          class: "text-center",
          thStyle: { width: "6%", minWidth: "60px" },
        },
      ],
    };
  },
  validations: {
    form: {
      poRefCode: { required, alphaNumUpperSymbol },
      vendorCode: { required, alphaNumUpperSymbol },
      vendorName: { required },
      items: {
        $each: {
          product: { required },
          quantity: { required },
          unit: { required },
          totalPrice: { required },
        },
      },
    },
  },
  computed: {
    ...mapGetters({
      getProductList: "moduleProductInventory/getProductList",
    }),
    selectedProductIds() {
      return this.form.items
        .map(item => item.product?.productId)
        .filter(id => id != null);
    },
  },
  methods: {
    ...mapActions({
      fetchGoodsReceiveList: "moduleGoodsReceive/fetchGoodsReceiveList",
    }),
    preventInvalidKey,
    formatPoRefCode(value) {
      this.form.poRefCode = formatCodeInput(value);
    },
    formatVendorCode(value) {
      this.form.vendorCode = formatCodeInput(value);
    },
    show(data = {}) {
      this.productList = data.productList || [];

      if (data.goodsReceive) {
        this.isEdit = true;
        this.editId = data.goodsReceive.id;
        this.mapFormData(data.goodsReceive);
        this.$bvModal.show("goodsReceiveItemModal");
        // Fetch details
        this.isLoading = true;
        this.$store
          .dispatch("moduleGoodsReceive/fetchGoodsReceiveById", {
            clinicUrl: this.$route.params.clinicUrl,
            branchUrl: this.$route.params.branchUrl,
            id: data.goodsReceive.id,
          })
          .then(detail => {
            if (detail) {
              this.mapFormData(detail);
            }
          })
          .catch(err => {
            console.error("fetchGoodsReceiveById failed", err);
          })
          .finally(() => {
            this.isLoading = false;
          });
      } else {
        this.isEdit = false;
        this.editId = null;
        this.resetForm();
        this.$bvModal.show("goodsReceiveItemModal");
      }
    },
    close() {
      this.$v.$reset();
      this.resetForm();
      this.isEdit = false;
      this.editId = null;
      this.$bvModal.hide("goodsReceiveItemModal");
    },
    resetForm() {
      this.form = {
        poRefCode: "",
        vendorCode: "",
        vendorName: "",
        items: [this.createEmptyItem()],
      };
    },
    createEmptyItem() {
      return {
        product: null,
        quantity: "",
        unit: null,
        totalPrice: "",
        lotNumber: "",
        expiredDt: null,
      };
    },
    addItem() {
      this.form.items.push(this.createEmptyItem());
    },
    removeItem(index) {
      this.form.items.splice(index, 1);
    },
    onProductSelected(index) {
      const item = this.form.items[index];
      if (item.product && item.product.productUnit) {
        item.unit = {
          id: item.product.productUnit.id,
          name: item.product.productUnit.description,
        };
      } else {
        item.unit = null;
      }
    },
    getUnitOptionsForRow(item) {
      if (!item.product) return [];
      const units = [];

      // Add baseUom first
      if (item.product.productUnit) {
        units.push({
          id: item.product.productUnit.id,
          name: item.product.productUnit.description,
        });
      }

      // Add all productUomConversions units
      const conversions = item.product.productConversionUnitItems || [];
      for (const c of conversions) {
        const uom = c.uom || c.productSubUnit || null;
        if (uom && uom.id && !units.find(u => u.id === uom.id)) {
          units.push({ id: uom.id, name: uom.description });
        }
      }

      return units;
    },
    getProductOptionsForRow(rowIndex) {
      const currentProductId = this.form.items[rowIndex]?.product?.productId;
      return this.productList.filter(
        p =>
          !this.selectedProductIds.includes(p.productId) ||
          p.productId === currentProductId
      );
    },
    productLabel(item) {
      if (!item) return "";
      return `${item.productCode} - ${item.productNameMain}`;
    },
    customLabel(item) {
      return item?.name || "";
    },
    saveGoodsReceive() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.isLoading = true;
      const body = {
        poRefCode: this.form.poRefCode,
        vendorCode: this.form.vendorCode,
        vendorName: this.form.vendorName,
        items: this.form.items.map(item => ({
          productId: item.product.productId,
          quantity: Number(item.quantity),
          uomId: item.unit.id,
          totalPrice: Number(item.totalPrice),
          lotNumber: item.lotNumber || "",
          expiredDt: item.expiredDt
            ? this.formatSaveDate(item.expiredDt)
            : null,
        })),
      };

      const action = this.isEdit
        ? "moduleGoodsReceive/updateGoodsReceive"
        : "moduleGoodsReceive/createGoodsReceive";

      const payload = {
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        body,
      };

      if (this.isEdit) {
        payload.id = this.editId;
      }

      this.$store
        .dispatch(action, payload)
        .then(res => {
          if (res.status === 200 || res.status === 201) {
            this.reloadList();
            this.close();
            this.$parent.showDialogToast(
              "success",
              this.isEdit
                ? "แก้ไขรายการใบรับสินค้าสำเร็จ"
                : "สร้างรายการใบรับสินค้าสำเร็จ"
            );
          }
        })
        .catch(err => {
          const errorMessage = err.response?.data?.errorMessage;
          const errorMsg =
            typeof errorMessage === "string"
              ? errorMessage
              : this.isEdit
              ? "แก้ไขใบรับสินค้าไม่สำเร็จ กรุณาลองใหม่"
              : "สร้างใบรับสินค้าไม่สำเร็จ กรุณาลองใหม่";
          this.$parent.showDialogToast("danger", errorMsg);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    formatSaveDate(date) {
      return moment(date).add(7, "h").format(); 
    },
    showDeleteConfirm() {
      this.$bvModal.show("deleteConfirmModalItem");
    },
    confirmDelete() {
      this.$bvModal.hide("deleteConfirmModalItem");

      this.isLoading = true;
      this.$store
        .dispatch("moduleGoodsReceive/deleteGoodsReceive", {
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          id: this.editId,
        })
        .then(res => {
          if (res.status === 200) {
            this.reloadList();
            this.close();
            this.$parent.showDialogToast(
              "success",
              "ลบรายการใบรับสินค้าสำเร็จ"
            );
          }
        })
        .catch(err => {
          const errorMessage = err.response?.data?.errorMessage;
          const errorMsg =
            typeof errorMessage === "string"
              ? errorMessage
              : "ลบใบรับสินค้าไม่สำเร็จ กรุณาลองใหม่";
          this.$parent.showDialogToast("danger", errorMsg);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    reloadList() {
      this.fetchGoodsReceiveList({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      });
    },
    mapFormData(data) {
      this.form.poRefCode = data.poRefCode || "";
      this.form.vendorCode = data.vendorCode || "";
      this.form.vendorName = data.vendorName || "";
      this.form.items = (data.items || []).map(item => {
        // Try to find full product from productList for dropdown matching
        const matchedProduct =
          this.productList.find(
            p =>
              p.productId ===
              (item.productId || (item.product && item.product.id))
          ) || null;

        // Build unit from available data
        let unit = null;
        if (item.uom) {
          unit = { id: item.uom.id, name: item.uom.description };
        } else if (item.uomId) {
          unit = { id: item.uomId, name: item.uomName || "" };
        }

        return {
          product: matchedProduct,
          quantity: item.quantity || "",
          unit,
          totalPrice: item.totalPrice || "",
          lotNumber: item.lotNumber || "",
          expiredDt: item.expiredDt ? new Date(item.expiredDt) : null,
        };
      });
      if (this.form.items.length === 0) {
        this.form.items = [this.createEmptyItem()];
      }
    },
    repositionDropdown(rowIndex) {
      this._repositionMultiselectInCell(rowIndex, "td-product-col");
    },
    repositionUnitDropdown(rowIndex) {
      this._repositionMultiselectInCell(rowIndex, "td-unit-col");
    },
    _repositionMultiselectInCell(rowIndex, cellClass) {
      const tableEl = this.$refs.goodsReceiveProductTable?.$el;
      if (!tableEl) return;
      const rows = tableEl.querySelectorAll("tbody tr");
      const row = rows[rowIndex];
      if (!row) return;
      const cell = row.querySelector(`td.${cellClass}`);
      if (!cell) return;
      const tags = cell.querySelector(".multiselect__tags");
      const dropdown = cell.querySelector(".multiselect__content-wrapper");
      if (!tags || !dropdown) return;
      const rect = tags.getBoundingClientRect();
      dropdown.style.position = "fixed";
      dropdown.style.top = rect.bottom + "px";
      dropdown.style.left = rect.left + "px";
      dropdown.style.width = rect.width + "px";
      dropdown.style.minWidth = rect.width + "px";
      dropdown.style.zIndex = "9999";
    },
    allowOnlyNumber(e) {
      const allowedKeys = [
        "Backspace",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Delete",
      ];
      if (allowedKeys.includes(e.key)) return;
      if (!/^[0-9]$/.test(e.key)) {
        e.preventDefault();
      }
    },
    allowOnlyDecimal(e) {
      const allowedKeys = [
        "Backspace",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "Delete",
      ];
      if (allowedKeys.includes(e.key)) return;
      if (e.key === "." && !e.target.value.includes(".")) return;
      if (!/^[0-9]$/.test(e.key)) {
        e.preventDefault();
      }
    },
  },
};
</script>

<style scoped>
.required-star {
  color: #dc3545;
  margin-left: 2px;
}

::v-deep .multiselect__tags {
  background-color: #ccecff6c;
  box-shadow: none;
}

::v-deep .multiselect--disabled .multiselect__tags {
  background-color: #e9ecef;
}

.required-input {
  background-color: #ccecff6c;
}

.required-input:disabled {
  background-color: #e9ecef;
}

::v-deep .multiselect__single,
::v-deep .multiselect__input,
::v-deep .multiselect__placeholder {
  background-color: transparent !important;
}

::v-deep span.multiselect__placeholder {
  margin-left: 6px !important;
}

::v-deep .multiselect__input,
::v-deep .multiselect__single {
  padding-left: 6px;
}

::v-deep span.multiselect__single {
  margin-left: 6px !important;
}

::v-deep .multiselect__single {
  margin-bottom: 0;
  padding-left: 0;
}

::v-deep #goodsReceiveProductTable tbody tr td {
  border-top: none !important;
  border-bottom: 1px solid #dee2e6 !important;
}

::v-deep #goodsReceiveProductTable tbody tr:last-child td {
  border-bottom: none !important;
}

::v-deep td {
  padding: 8px;
}

::v-deep .table-responsive {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 0;
}

::v-deep #goodsReceiveProductTable {
  min-width: 750px;
}

::v-deep #goodsReceiveProductTable .mx-datepicker {
  width: 100%;
}

::v-deep .bottom-add-row {
  text-align: left !important;
  padding: 8px 24px;
}

::v-deep #goodsReceiveProductTable.table-hover tbody tr:last-child:hover {
  background-color: transparent !important;
}

::v-deep .append-text {
  max-width: 100px;
  width: 100%;
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: center;
}

::v-deep #goodsReceiveProductTable thead tr th:nth-child(1),
::v-deep #goodsReceiveProductTable tbody tr:not(:last-child) td:nth-child(1) {
  text-align: center !important;
  vertical-align: middle !important;
}

/* Product column: show full text, row grows */
::v-deep #goodsReceiveProductTable td.td-product-col .multiselect__single {
  white-space: normal;
  word-break: break-word;
}

/* Unit column: show as ellipsis */
::v-deep #goodsReceiveProductTable td.td-unit-col .multiselect__single {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: 150px;
}

::v-deep #goodsReceiveProductTable .bottom-row-cell {
  text-align: left !important;
}

::v-deep .modal-footer .btn {
  padding: 8px 24px;
}

::v-deep .component-modal-product .modal-dialog {
  max-width: 1300px;
}

@media (max-width: 576px) {
  ::v-deep .modal-dialog {
    margin: 0.5rem;
    max-width: calc(100% - 1rem);
  }

  ::v-deep .modal-footer {
    flex-direction: column-reverse;
    gap: 0.5rem;
  }

  ::v-deep .modal-footer .btn {
    width: 100%;
    margin: 0 !important;
  }
}
</style>
